"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { type Control, useForm, useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import TextareaAutosize from "react-textarea-autosize";

import { RevealGroup, RevealItem } from "@/components/motion/reveal";
import {
  LineField,
  lineControlClassName,
  lineControlProps,
} from "@/components/shared/form/line-field";
import {
  SubmitButton,
  type SubmitState,
} from "@/components/shared/form/submit-button";
import { cn } from "@/lib/utils";

import { sendContactMessage } from "../_actions/contact.action";
import {
  CONTACT_LIMITS,
  type ContactSchema,
  contactSchema,
  type ContactSchemaInput,
  toContactErrorKey,
} from "../_schemas/contact";

// =============================================================================

const DEFAULT_VALUES: ContactSchemaInput = {
  firstName: "",
  lastName: "",
  email: "",
  subject: "",
  message: "",
  website: "",
};

/** Time the "Message sent" state stays on the button */
const SUCCESS_DURATION = 2800;

/**
 * Contact form: validated with zod on the client and again by the server
 * action, which stores the message and sends the emails.
 */
export function ContactForm() {
  const t = useTranslations("Connect.form");
  const locale = useLocale();
  const [state, setState] = useState<SubmitState>("idle");

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<ContactSchemaInput, unknown, ContactSchema>({
    resolver: zodResolver(contactSchema),
    defaultValues: DEFAULT_VALUES,
    mode: "onTouched",
  });

  useEffect(() => {
    if (state !== "success") return;
    const timeout = setTimeout(() => setState("idle"), SUCCESS_DURATION);
    return () => clearTimeout(timeout);
  }, [state]);

  const errorOf = (field: keyof ContactSchemaInput) => {
    const message = errors[field]?.message;
    return message ? t(`errors.${toContactErrorKey(message)}`) : undefined;
  };

  const onSubmit = handleSubmit(async (values) => {
    if (state !== "idle") return;
    setState("loading");

    try {
      const response = await sendContactMessage(locale, values);
      if (!response.success) {
        toast.error(response.message);
        setState("idle");
        return;
      }
      toast.success(response.message);
      reset(DEFAULT_VALUES);
      setState("success");
    } catch {
      toast.error(t("errors.generic"));
      setState("idle");
    }
  });

  const fields = [
    {
      name: "firstName",
      id: "contact-first-name",
      autoComplete: "given-name",
    },
    {
      name: "lastName",
      id: "contact-last-name",
      autoComplete: "family-name",
    },
    {
      name: "email",
      id: "contact-email",
      autoComplete: "email",
      type: "email",
      wide: true,
    },
    { name: "subject", id: "contact-subject", autoComplete: "off", wide: true },
  ] as const;

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      aria-label={t("label")}
      className="relative"
    >
      <RevealGroup
        stagger={0.07}
        delay={0.35}
        immediate
        className="grid gap-x-6 gap-y-[clamp(2.75rem,4.45vw,4rem)] md:grid-cols-2"
      >
        {fields.map((field) => {
          const error = errorOf(field.name);
          return (
            <RevealItem
              key={field.name}
              className={cn("wide" in field && field.wide && "md:col-span-2")}
            >
              <LineField
                htmlFor={field.id}
                label={t(`${field.name}.label`)}
                required
                error={error}
              >
                <input
                  {...register(field.name)}
                  {...lineControlProps(field.id, error)}
                  type={"type" in field ? field.type : "text"}
                  inputMode={field.name === "email" ? "email" : undefined}
                  autoComplete={field.autoComplete}
                  aria-required
                  placeholder={t(`${field.name}.placeholder`)}
                  className={lineControlClassName}
                />
              </LineField>
            </RevealItem>
          );
        })}

        <RevealItem className="md:col-span-2">
          <LineField
            htmlFor="contact-message"
            label={t("message.label")}
            required
            error={errorOf("message")}
            aside={<MessageCounter control={control} />}
          >
            <TextareaAutosize
              {...register("message")}
              {...lineControlProps("contact-message", errorOf("message"))}
              aria-required
              minRows={1}
              maxRows={14}
              maxLength={CONTACT_LIMITS.message}
              placeholder={t("message.placeholder")}
              className={lineControlClassName}
            />
          </LineField>
        </RevealItem>

        <RevealItem className="pt-[clamp(0.25rem,1vw,1rem)] md:col-span-2">
          <SubmitButton
            state={state}
            label={t("submit")}
            loadingLabel={t("submitting")}
            successLabel={t("sent")}
          />
        </RevealItem>
      </RevealGroup>

      {/* Honeypot: invisible for visitors and assistive technologies */}
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[9999px] size-px overflow-hidden opacity-0"
      >
        <label>
          {t("honeypot")}
          <input
            {...register("website")}
            type="text"
            tabIndex={-1}
            autoComplete="off"
          />
        </label>
      </div>
    </form>
  );
}

// =============================================================================

/**
 * Character counter of the message, shown when it gets close to the limit.
 */
function MessageCounter({
  control,
}: {
  control: Control<ContactSchemaInput, unknown, ContactSchema>;
}) {
  const t = useTranslations("Connect.form.message");
  const message = useWatch({ control, name: "message" }) ?? "";
  const isVisible = message.length > CONTACT_LIMITS.message * 0.8;

  return (
    <span
      aria-live="polite"
      className={cn(
        "pb-1 text-sm text-muted-foreground tabular-nums transition-opacity duration-500",
        isVisible ? "opacity-100" : "opacity-0"
      )}
    >
      {isVisible &&
        t("counter", {
          count: message.length,
          max: CONTACT_LIMITS.message,
        })}
    </span>
  );
}

// =============================================================================
